import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, download, IMPORT_STATUS, ImportJob, ImportRow, ImportSheet } from '../api';
import { useSession } from '../session';
import { ErrorBox, fmtDate, useAsync } from '../components/ui';

const RUNNING = new Set(['validating', 'committing', 'reverting']);
const ACTION: Record<string, string> = { create: 'új', update: 'frissül', skip: 'kihagy' };
const TARGET_PATH: Record<string, string> = { company: '/cegek', contact: '/kontaktok', deal: '/ertekesites' };
const cellText = (v: unknown) => (v === null || v === undefined ? '' : typeof v === 'boolean' ? (v ? 'igen' : 'nem') : String(v));

/** Import varázsló: hozzárendelés → ellenőrzés és előnézet → jóváhagyás és véglegesítés → (visszavonás). */
export function ImportJobPage() {
  const { id } = useParams();
  const s = useSession();
  const [job, setJob] = useState<ImportJob | null>(null);
  const [sheets, setSheets] = useState<ImportSheet[]>([]);
  const [onMatch, setOnMatch] = useState<'update' | 'skip'>('update');
  const [dirty, setDirty] = useState(false);
  const [tab, setTab] = useState<'problems' | 'warnings' | 'ok'>('problems');
  const [rows, setRows] = useState<{ items: ImportRow[]; total: number } | null>(null);
  const [page, setPage] = useState(0);
  const load = useAsync();
  const act = useAsync();
  const timer = useRef<number>();

  const reload = () => load.run(async () => {
    const j = await api<ImportJob>(`/imports/${id}`);
    setJob(j);
    setSheets((cur) => (dirty && cur.length ? cur : j.sheets));
    setOnMatch(j.options.on_match ?? 'update');
    window.clearTimeout(timer.current);
    if (RUNNING.has(j.status)) timer.current = window.setTimeout(reload, 1500);
  });
  useEffect(() => { setDirty(false); reload(); return () => window.clearTimeout(timer.current); }, [id]);
  const done = job && ['validated', 'committed', 'failed', 'reverted'].includes(job.status) && (job.summary.ok ?? 0) + (job.summary.error ?? 0) > 0;
  useEffect(() => {
    if (!done) { setRows(null); return; }
    api<{ items: ImportRow[]; total: number }>(`/imports/${id}/rows?status=${tab === 'ok' ? (job!.status === 'validated' ? 'ok' : job!.status === 'reverted' ? 'reverted' : 'done') : tab}&limit=50&offset=${page * 50}`).then(setRows).catch(() => setRows(null));
  }, [id, job?.status, job?.progress, tab, page, done]);

  const catalog = job?.catalog ?? [];
  const fieldsOf = (type: string | null) => catalog.find((c) => c.type === type)?.fields ?? [];
  const sheetName = (i: number) => sheets.find((x) => x.index === i)?.name ?? `${i + 1}. lap`;
  const editable = job && ['uploaded', 'validated', 'failed'].includes(job.status) && !job.committed_at;
  const canCommit = s.role === 'owner';

  const setType = (idx: number, type: string | null) => {
    setDirty(true);
    setSheets(sheets.map((x) => (x.index === idx ? { ...x, type, mapping: type === job!.sheets.find((o) => o.index === idx)?.type ? x.mapping : {} } : x)));
  };
  const setField = (idx: number, col: number, key: string) => {
    setDirty(true);
    setSheets(sheets.map((x) => {
      if (x.index !== idx) return x;
      const m = Object.fromEntries(Object.entries(x.mapping).filter(([c, k]) => Number(c) !== col && (!key || k !== key)));
      if (key) m[col] = key;
      return { ...x, mapping: m };
    }));
  };
  const saveAndValidate = async () => {
    const ok = await act.run(async () => {
      if (dirty || job!.status !== 'uploaded' || onMatch !== (job!.options.on_match ?? 'update'))
        await api(`/imports/${id}/mapping`, { method: 'PUT', body: { sheets: sheets.map((x) => ({ index: x.index, type: x.type, mapping: x.mapping })), options: { on_match: onMatch } } });
      return api(`/imports/${id}/validate`, { method: 'POST' });
    });
    if (ok) { setDirty(false); setPage(0); setTab('problems'); reload(); }
  };
  const commit = async () => {
    if (!window.confirm(`Véglegesíted az importot? ${job!.summary.create ?? 0} új rekord és ${job!.summary.update ?? 0} frissítés kerül be. 30 napig visszavonható.`)) return;
    if (await act.run(() => api(`/imports/${id}/commit`, { method: 'POST' }))) reload();
  };
  const revert = async () => {
    if (!window.confirm('Visszavonod a teljes importot? A létrehozott rekordok törlődnek, a módosítások visszaállnak (ha azóta nem írták át őket).')) return;
    if (await act.run(() => api(`/imports/${id}/revert`, { method: 'POST' }))) reload();
  };

  if (load.error && !job) return <ErrorBox error={load.error} />;
  if (!job) return <div className="muted">Betöltés…</div>;
  const sum = job.summary;
  const step = job.status === 'committed' || job.status === 'reverted' || job.status === 'committing' || job.status === 'reverting' ? 3 : job.status === 'validated' || job.status === 'validating' ? 2 : 1;
  const pct = job.total_rows ? Math.min(100, Math.round((job.progress / job.total_rows) * 100)) : 0;

  return (
    <div>
      <div className="page-head">
        <h2>{job.file_name} <span className={`badge status-${job.status}`}>{IMPORT_STATUS[job.status] ?? job.status}</span></h2>
        <div className="spacer" />
        <Link to="/import">← Importok</Link>
      </div>
      <p className="muted small">{job.total_rows} sor · feltöltötte {job.created_by_name ?? '–'}, {fmtDate(job.created_at)}
        {job.mapping_by.startsWith('agent:') && <> · <span className="badge badge-agent">a hozzárendelést ügynök javasolta</span></>}
        {job.committed_at && <> · véglegesítve {fmtDate(job.committed_at)}</>}{job.reverted_at && <> · visszavonva {fmtDate(job.reverted_at)}</>}</p>
      <ol className="stepper static" aria-label="Lépések">
        {['Hozzárendelés', 'Ellenőrzés és előnézet', 'Véglegesítés'].map((t, i) => <li key={t} className={i + 1 === step ? 'current' : i + 1 < step ? 'past' : ''}><span>{i + 1}. {t}</span></li>)}
      </ol>
      {job.error && <div className="error">A feldolgozás megszakadt: {job.error}</div>}
      {RUNNING.has(job.status) && (
        <section className="card">
          <h3>{IMPORT_STATUS[job.status]}</h3>
          <div className="progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}><div style={{ width: `${pct}%` }} /></div>
          <p className="muted small">{job.progress} / {job.total_rows} sor. Nyugodtan elnavigálhatsz, a feldolgozás a háttérben fut.</p>
        </section>
      )}

      {editable && (
        <section className="card">
          <div className="card-head">
            <h3>Lapok és oszlopok</h3>
            <span className="muted small">A rendszer a fejlécek alapján javasolt; ahol nem jó, válaszd ki a megfelelő mezőt, vagy hagyd ki az oszlopot.</span>
          </div>
          {sheets.map((sh) => {
            const fields = fieldsOf(sh.type);
            const used = new Set(Object.values(sh.mapping));
            const missing = fields.filter((f) => f.required && !used.has(f.key)).map((f) => f.label);
            if (sh.type === 'contact' && !used.has('last_name') && !used.has('full_name')) missing.push('Név vagy Vezetéknév');
            return (
              <div key={sh.index} className="sheet-map">
                <div className="row sheet-head">
                  <strong>{sh.name}</strong> <span className="muted">{sh.row_count} sor</span>
                  <select aria-label={`${sh.name} típusa`} value={sh.type ?? ''} onChange={(e) => setType(sh.index, e.target.value || null)}>
                    <option value="">– kihagyom ezt a lapot –</option>
                    {catalog.map((c) => <option key={c.type} value={c.type}>{c.label}</option>)}
                  </select>
                </div>
                {sh.type && missing.length > 0 && <div className="notice">Hiányzik oszlop ehhez: {missing.join(', ')}</div>}
                {sh.type && (
                  <table className="list mapping">
                    <thead><tr><th>Oszlop a fájlban</th><th>Minta</th><th>Mező a CRM-ben</th></tr></thead>
                    <tbody>
                      {sh.header.map((h, col) => {
                        const key = sh.mapping[col] ?? '';
                        const f = fields.find((x) => x.key === key);
                        return (
                          <tr key={col}>
                            <td><strong>{h || <span className="muted">({col + 1}. oszlop, fejléc nélkül)</span>}</strong></td>
                            <td className="muted small sample">{(sh.sample ?? job.sheets.find((o) => o.index === sh.index)?.sample ?? []).slice(0, 3).map((r) => cellText(r.cells[col])).filter(Boolean).join(' · ') || '–'}</td>
                            <td>
                              <select value={key} onChange={(e) => setField(sh.index, col, e.target.value)} aria-label={`${h} mezője`}>
                                <option value="">– kihagyom –</option>
                                {fields.map((x) => <option key={x.key} value={x.key}>{x.label}{x.required ? ' *' : ''}{used.has(x.key) && x.key !== key ? ' (már használt)' : ''}</option>)}
                              </select>
                              {f?.hint && <div className="muted small">{f.hint}</div>}
                              {key && sh.suggested[col] === key && <span className="badge">javaslat</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
          <div className="field">
            <span>Ha a rekord már létezik (azonos azonosító, adószám vagy e-mail)</span>
            <label className="check"><input type="radio" checked={onMatch === 'update'} onChange={() => { setOnMatch('update'); setDirty(true); }} /> frissítse a kitöltött mezőket</label>
            <label className="check"><input type="radio" checked={onMatch === 'skip'} onChange={() => { setOnMatch('skip'); setDirty(true); }} /> hagyja változatlanul</label>
          </div>
          <ErrorBox error={act.error} />
          <div className="actions">
            <button onClick={saveAndValidate} disabled={act.busy}>{job.status === 'validated' && !dirty ? 'Újraellenőrzés' : 'Ellenőrzés és előnézet'}</button>
            {dirty && <span className="muted small">Nem mentett módosítás</span>}
          </div>
        </section>
      )}

      {done && (
        <section className="card">
          <h3>{job.status === 'validated' ? 'Előnézet' : 'Eredmény'}</h3>
          <div className="tiles">
            {job.status === 'validated' ? <>
              <div className="tile"><b>{sum.create ?? 0}</b><span>új</span></div>
              <div className="tile"><b>{sum.update ?? 0}</b><span>frissül</span></div>
              <div className="tile"><b>{sum.skip ?? 0}</b><span>kihagy</span></div>
            </> : <>
              <div className="tile"><b>{(sum.done ?? 0) + (sum.reverted ?? 0)}</b><span>{job.status === 'reverted' ? 'visszavonva' : 'feldolgozva'}</span></div>
              <div className="tile"><b>{sum.create ?? 0}</b><span>új</span></div>
              <div className="tile"><b>{sum.update ?? 0}</b><span>frissült</span></div>
            </>}
            <div className={`tile ${sum.error ? 'bad' : ''}`}><b>{sum.error ?? 0}</b><span>hibás</span></div>
            <div className="tile"><b>{sum.warnings ?? 0}</b><span>figyelmeztetés</span></div>
            {(sum.ignored ?? 0) > 0 && <div className="tile"><b>{sum.ignored}</b><span>kihagyott lapon</span></div>}
          </div>
          <div className="actions">
            {job.status === 'validated' && canCommit && <button onClick={commit} disabled={act.busy || !(sum.ok ?? 0)}>Jóváhagyás és véglegesítés</button>}
            {job.status === 'validated' && !canCommit && <span className="muted small">Véglegesíteni a tulajdonos tud.</span>}
            {(sum.error ?? 0) > 0 && <button className="secondary" onClick={() => act.run(() => download(`/imports/${id}/errors`))}>Hibás sorok letöltése</button>}
            {['committed', 'failed'].includes(job.status) && job.committed_at !== null && canCommit && (sum.done ?? 0) > 0 &&
              <button className="danger" onClick={revert} disabled={act.busy}>Teljes import visszavonása</button>}
          </div>
          {job.status === 'validated' && (sum.error ?? 0) > 0 && <p className="muted small">A hibás sorok kimaradnak. Javítsd őket a letöltött fájlban (az első oszlop a magyarázat), és töltsd fel újra – ami már bekerült, nem duplikálódik.</p>}
          {!editable && <ErrorBox error={act.error} />}
          <div className="tabs" role="tablist">
            {([['problems', `Hibák (${sum.error ?? 0})`], ['warnings', `Figyelmeztetések (${sum.warnings ?? 0})`], ['ok', job.status === 'validated' ? 'Rendben lévő sorok' : 'Feldolgozott sorok']] as const).map(([k, l]) => (
              <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? '' : 'secondary'} onClick={() => { setTab(k as any); setPage(0); }}>{l}</button>
            ))}
          </div>
          <RowsTable rows={rows} sheetName={sheetName} sheets={sheets} tab={tab} status={job.status} />
          {rows && rows.total > 50 && (
            <div className="pager">
              <button className="secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>← Előző</button>
              <span>{page * 50 + 1}–{Math.min((page + 1) * 50, rows.total)} / {rows.total}</span>
              <button className="secondary" disabled={(page + 1) * 50 >= rows.total} onClick={() => setPage(page + 1)}>Következő →</button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function RowsTable({ rows, sheetName, sheets, tab, status }: { rows: { items: ImportRow[]; total: number } | null; sheetName: (i: number) => string; sheets: ImportSheet[]; tab: string; status: string }) {
  const preview = useMemo(() => (r: ImportRow) => {
    const sh = sheets.find((x) => x.index === r.sheet);
    const cols = Object.keys(sh?.mapping ?? {}).map(Number).slice(0, 4);
    return cols.map((c) => cellText(r.data[c])).filter(Boolean).join(' · ') || r.data.slice(0, 3).map(cellText).filter(Boolean).join(' · ');
  }, [sheets]);
  if (!rows) return <p className="muted">Betöltés…</p>;
  if (!rows.items.length) return <p className="muted">{tab === 'problems' ? 'Nincs hibás sor.' : tab === 'warnings' ? 'Nincs figyelmeztetés.' : 'Nincs ilyen sor.'}</p>;
  const st = new Set(['done', 'reverted']);
  return (
    <table className="list rows">
      <thead><tr><th>Lap, sor</th><th>Adat</th><th>{tab === 'ok' ? 'Művelet' : 'Magyarázat'}</th></tr></thead>
      <tbody>
        {rows.items.map((r) => (
          <tr key={`${r.sheet}-${r.row_no}`}>
            <td className="nowrap">{sheetName(r.sheet)} · {r.row_no}.</td>
            <td className="small">{preview(r)}</td>
            <td className="small">
              {tab === 'ok' && r.action && <span className="badge">{ACTION[r.action]}</span>}
              {tab === 'ok' && r.status === 'reverted' && <span className="badge">visszavonva</span>}
              {tab === 'ok' && st.has(r.status) && r.target_id && TARGET_PATH[r.target_type ?? ''] && status !== 'reverted' && r.action !== 'skip' &&
                <Link to={`${TARGET_PATH[r.target_type!]}/${r.target_id}`}> megnyitás</Link>}
              {(r.errors ?? []).map((e, i) => <div key={i} className="err-line"><strong>{e.field}:</strong> {e.message}</div>)}
              {(tab !== 'problems') && (r.warnings ?? []).map((e, i) => <div key={i} className="muted"><strong>{e.field}:</strong> {e.message}</div>)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
